import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle2, ArrowRight, Shield, AlertTriangle, XCircle, FileText } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/utils/logger';

import { CompletionActions } from './CompletionActions';

const ASSESSMENT_NAMES: Record<string, string> = {
  'owner-readiness': 'Owner Readiness Assessment',
  'succession-readiness': 'Succession Readiness Assessment',
  'business-profile': 'Business Profile Assessment',
  'business-valuation': 'Business Valuation Assessment',
  'business-optimization': 'Business Optimization Review',
  'advisory-team': 'Advisory Team Assembly',
  'financial-readiness': 'Financial Readiness Assessment',
  'succession-strategy': 'Succession Strategy Assessment',
  'governance-family': 'Governance & Family Dynamics',
  'exit-structure': 'Exit Structure & Tax Optimization',
  'client-team-transition': 'Client & Team Transition',
  'mysuccession-plan': 'Succession Plan Assembly',
};

interface DbAssessmentCompletionScreenProps {
  question: { id: string; type: string; title: string };
  onFinish: () => void;
  onNextAssessment?: () => void;
  moduleId?: string;
}

function LcgeFlag() {
  return (
    <div className="w-full max-w-lg mx-auto rounded-lg border-2 border-amber-500/60 bg-amber-50/50 dark:bg-amber-950/20 p-5 text-left space-y-2">
      <div className="flex items-start gap-3">
        <Shield className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground">
            You may qualify for the Lifetime Capital Gains Exemption
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Based on your answers, a share sale structure could shelter a significant portion of your capital gains from tax — potentially over $1.25M. Your Advisory Team Assessment will help ensure you have the right tax professionals in place to maximize this benefit.
          </p>
          <p className="text-xs text-muted-foreground/80 italic">
            This has been noted in your succession plan.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Due Diligence Readiness (A10) ──────────────────────
interface DueDiligenceItem {
  label: string;
  questionKey: string;
  readyValues: string[];
  amberValues?: string[];
}

const DUE_DILIGENCE_ITEMS: DueDiligenceItem[] = [
  { label: 'Legal Documents', questionKey: 'legal_review_readiness', readyValues: ['organized_ready', 'most_accessible'], amberValues: ['some_accessible', 'partially_organized'] },
  { label: 'Compliance Docs', questionKey: 'compliance_documentation', readyValues: ['comprehensive_manual', 'documented_regular'], amberValues: ['basic_documentation', 'some_processes'] },
  { label: 'IP Documented', questionKey: 'ip_asset_inventory', readyValues: ['all_documented', 'most_documented'], amberValues: ['some_documented', 'partially_identified'] },
  { label: 'Privacy Ready', questionKey: 'pipeda_compliance', readyValues: ['documented_ready', 'general_awareness'], amberValues: ['basic_awareness', 'some_measures'] },
];

type ReadinessLevel = 'ready' | 'amber' | 'gap';

function getReadiness(answer: string | undefined, item: DueDiligenceItem): ReadinessLevel {
  if (!answer) return 'gap';
  if (item.readyValues.includes(answer)) return 'ready';
  if (item.amberValues?.includes(answer)) return 'amber';
  return 'gap';
}

function DueDiligenceReadiness({ answers }: { answers: Record<string, unknown> }) {
  const items = DUE_DILIGENCE_ITEMS.map((item) => ({
    ...item,
    level: getReadiness(answers[item.questionKey] as string | undefined, item),
  }));

  return (
    <div className="w-full max-w-lg mx-auto rounded-lg border border-border bg-card p-5 text-left space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-foreground">Your Due Diligence Readiness</h3>
        <p className="text-xs text-muted-foreground mt-1">Buyers will request these items early in the process.</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {items.map((item) => (
          <div key={item.questionKey} className="flex items-center gap-2 text-sm">
            {item.level === 'ready' && <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />}
            {item.level === 'amber' && <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />}
            {item.level === 'gap' && <XCircle className="h-4 w-4 text-destructive shrink-0" />}
            <span className="text-foreground">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DbAssessmentCompletionScreen({ question, onFinish, onNextAssessment, moduleId }: DbAssessmentCompletionScreenProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [totalScore, setTotalScore] = useState<number | null>(null);
  const [showLcge, setShowLcge] = useState(false);
  const [exitStructureAnswers, setExitStructureAnswers] = useState<Record<string, unknown> | null>(null);

  const assessmentKey = moduleId || question.id.replace(/-completion$/, '');
  const assessmentName = ASSESSMENT_NAMES[assessmentKey] || 'Assessment';

  useEffect(() => {
    if (!user?.id) return;

    const fetchScore = async () => {
      try {
        const { data, error } = await (supabase as any)
          .from('user_scores')
          .select('score_value')
          .eq('user_id', user.id)
          .eq('entity_type', 'overall')
          .maybeSingle();

        if (error) throw error;
        if (data?.score_value != null) {
          setTotalScore(data.score_value);
        }
      } catch (error) {
        logger.error('Error fetching score on completion:', error);
      }
    };

    fetchScore();
  }, [user?.id]);

  // LCGE flag check for business-valuation
  useEffect(() => {
    if (!user?.id || assessmentKey !== 'business-valuation') return;

    const checkLcge = async () => {
      try {
        const { data, error } = await supabase
          .from('assessment_progress')
          .select('answers')
          .eq('user_id', user.id)
          .eq('module_id', 'business-valuation')
          .maybeSingle();

        if (error || !data?.answers) return;

        const answers = data.answers as Record<string, unknown>;
        const txType = answers.transaction_type as string | undefined;
        const lcge = answers.lcge_assessment as string | undefined;

        const txEligible = txType === 'share_sale' || txType === 'not_sure';
        const lcgeEligible = lcge === 'confirmed_eligible' || lcge === 'likely_eligible';

        setShowLcge(txEligible && lcgeEligible);
      } catch (error) {
        logger.error('Error checking LCGE eligibility:', error);
      }
    };

    checkLcge();
  }, [user?.id, assessmentKey]);

  // Due diligence readiness for exit-structure
  useEffect(() => {
    if (!user?.id || assessmentKey !== 'exit-structure') return;

    const fetchA10Answers = async () => {
      try {
        const { data, error } = await supabase
          .from('assessment_progress')
          .select('answers')
          .eq('user_id', user.id)
          .eq('module_id', 'exit-structure')
          .maybeSingle();

        if (error || !data?.answers) return;
        setExitStructureAnswers(data.answers as Record<string, unknown>);
      } catch (error) {
        logger.error('Error fetching A10 answers:', error);
      }
    };

    fetchA10Answers();
  }, [user?.id, assessmentKey]);

  return (
    <div className="text-center space-y-8 py-8 w-full max-w-4xl mx-auto min-h-[400px] flex flex-col items-center justify-center">
      <div className="flex justify-center">
        <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
          <CheckCircle2 className="h-8 w-8 text-primary" />
        </div>
      </div>

      <div className="space-y-3">
        <h2 className="text-2xl font-semibold text-foreground">
          {assessmentName} Complete
        </h2>

        {totalScore != null && (
          <p className="text-lg text-muted-foreground">
            Your mySuccession Score: <span className="font-bold text-foreground">{totalScore}%</span>
          </p>
        )}
        <p className="text-muted-foreground max-w-lg mx-auto">
          Your succession plan has been updated. View your dashboard to see your progress and next recommended steps.
        </p>
      </div>

      {showLcge && <LcgeFlag />}

      {assessmentKey === 'exit-structure' && exitStructureAnswers && (
        <DueDiligenceReadiness answers={exitStructureAnswers} />
      )}

      <div className="flex flex-col items-center gap-3">
        {assessmentKey === 'exit-structure' && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/documents')}
            className="border-primary text-primary hover:bg-primary/5 text-sm gap-1.5"
          >
            <FileText className="h-4 w-4" />
            Review your Exit Structure checklist →
          </Button>
        )}

        <CompletionActions
          onNextAssessment={onNextAssessment || onFinish}
          onViewDashboard={onFinish}
        />
      </div>
    </div>
  );
}
